import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { BrowserMultiFormatReader } from '@zxing/library';
import { ouvrirFenetre, fermerFenetre } from '../utils/pauseGlobale';
import { ScanLine, Keyboard, Trash2, Check, X, Copy, AlertCircle, Pencil, Lock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const TechCodesBarres = ({ chantierId, verrouille, bloque, messageBloque, onChange }) => {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');
  const [scanOuvert, setScanOuvert] = useState(false);
  const [typeEnCours, setTypeEnCours] = useState('micro_onduleur');
  const [saisie, setSaisie] = useState('');
  const [saisieOuverte, setSaisieOuverte] = useState(false);
  const video = useRef(null);
  const lecteur = useRef(null);
  const vus = useRef(new Set());
  const typeRef = useRef('micro_onduleur');
  const [parMicro, setParMicro] = useState(2);
  const [msgConfig, setMsgConfig] = useState('');
  const [confirme, setConfirme] = useState(null);
  const [modif, setModif] = useState(null);
  const [nouveau, setNouveau] = useState('');
  const [aForcer, setAForcer] = useState(null);
  const [aRemplacer, setARemplacer] = useState(null);
  const [masques, setMasques] = useState([]);
  const [provisoires, setProvisoires] = useState([]);

  const charger = async () => {
    try {
      const r = await axios.get(`${API}/api/chantiers/${chantierId}/codes-barres`);
      setData(r.data.data);
      setMasques([]);
      const tous = [...(r.data.data.micro_onduleurs || []), ...(r.data.data.passerelles || [])].map((c) => c.numero);
      setProvisoires((p) => p.filter((x) => !tous.includes(x.numero)));
      if (onChange) onChange();
      if (r.data.data?.panneaux_par_micro) setParMicro(r.data.data.panneaux_par_micro);
    } catch (e) {
      setErreur('Impossible de charger les numéros');
    }
  };

  useEffect(() => { charger(); }, [chantierId]);

  const enregistrer = async (numero, manuelle) => {
    setErreur('');
    try {
      await axios.post(`${API}/api/chantiers/${chantierId}/codes-barres`, {
        numero, type_materiel: typeRef.current, saisie_manuelle: manuelle
      });
      charger();
      return true;
    } catch (e) {
      if (e.response?.status === 409) {
        setAForcer({ numero: numero, type: typeRef.current, manuelle: manuelle, message: e.response.data.detail });
      } else {
        setErreur(e.response?.data?.detail || "Échec de l'enregistrement");
      }
      setSucces('');
      return false;
    }
  };

  const demarrerScan = async (type) => {
    ouvrirFenetre();
    typeRef.current = type;
    setTypeEnCours(type);
    setErreur('');
    setScanOuvert(true);
    setTimeout(async () => {
      try {
        vus.current = new Set();
        lecteur.current = new BrowserMultiFormatReader();
        await lecteur.current.decodeFromConstraints(
          {
            video: {
              facingMode: { ideal: 'environment' },
              width: { ideal: 1920 },
              height: { ideal: 1080 },
              focusMode: 'continuous',
              advanced: [{ focusMode: 'continuous' }, { zoom: 2 }]
            }
          },
          video.current,
          async (res) => {
            if (res) {
              const txt = res.getText();
              const propre = txt.toUpperCase().replace(/[^A-Z0-9]/g, '');
              if (vus.current.has(propre)) return;
              vus.current.add(propre);
              let ok;
              if (aRemplacer) {
                try {
                  await axios.put(`${API}/api/chantiers/${chantierId}/codes-barres/${aRemplacer}`, { numero: propre });
                  setARemplacer(null);
                  charger();
                  ok = true;
                } catch (e) {
                  if (e.response?.status === 409) {
                    setAForcer({ numero: propre, type: typeRef.current, manuelle: false, message: e.response.data.detail, remplace: aRemplacer });
                  } else {
                    setErreur(e.response?.data?.detail || "Échec du remplacement");
                  }
                  ok = false;
                }
              } else {
                ok = await enregistrer(txt, false);
              }
              if (ok) {
                if (navigator.vibrate) navigator.vibrate(120);
                setConfirme(propre);
                setTimeout(() => { setConfirme(null); arreterScan(); }, 1000);
              }
            }
          }
        );
      } catch (e) {
        setErreur("Impossible d'accéder à l'appareil photo");
        setScanOuvert(false);
      }
    }, 100);
  };

  const arreterScan = () => {
    fermerFenetre();
    setARemplacer(null);
    try { lecteur.current?.reset(); } catch (e) {}
    setScanOuvert(false);
  };

  useEffect(() => () => { try { lecteur.current?.reset(); } catch (e) {} }, []);

  const supprimer = async (id) => {
    try {
      await axios.delete(`${API}/api/chantiers/${chantierId}/codes-barres/${id}`);
      charger();
    } catch (e) {
      setErreur('Échec de la suppression');
    }
  };

  const copier = () => {
    navigator.clipboard?.writeText(data?.texte_copiable || '');
    setSucces('Liste copiée');
    setTimeout(() => setSucces(''), 2000);
  };

  if (!data) return <div style={{ padding: '20px', textAlign: 'center', color: '#9CA3AF' }}>Chargement…</div>;

  const btnVert = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '13px', border: 'none', borderRadius: '10px', background: '#10B981', color: '#fff', fontSize: '15px', cursor: 'pointer', width: '100%' };
  const btnGris = { ...btnVert, background: '#fff', color: '#374151', border: '1px solid #D1D5DB' };
  const microsIncomplets = !data.nb_attendus || data.nb_micro_onduleurs < data.nb_attendus;

  const bloc = { border: '1px solid #E5E7EB', borderRadius: '10px', padding: '14px', marginBottom: '14px' };

  const ligne = (c) => masques.includes(c.id) ? null : (
    <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '9px 0', borderTop: '1px solid #F3F4F6' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '15px', fontFamily: 'monospace', letterSpacing: '0.5px' }}>{c.numero}</div>
        <div style={{ fontSize: '11px', color: '#9CA3AF' }}>
          {c.saisie_manuelle ? 'saisi à la main' : 'scanné'}{c.par ? ` · ${c.par}` : ''}
        </div>
      </div>
      {!verrouille && (
        <div style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
          <button onClick={() => { if (navigator.vibrate) navigator.vibrate(30); setModif(c); setNouveau(c.numero); setErreur(''); }}
            style={{ width: '38px', height: '38px', borderRadius: '9px', border: '1px solid #E5E7EB', background: '#fff', color: '#6B7280', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
            <Pencil size={16} />
          </button>
          <button onClick={() => { if (navigator.vibrate) navigator.vibrate(30); setARemplacer(c.id); demarrerScan(c.type_materiel || 'micro_onduleur'); }}
            style={{ width: '38px', height: '38px', borderRadius: '9px', border: '1px solid #A7F3D0', background: '#fff', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
            <ScanLine size={16} />
          </button>
          <button onClick={() => {
              if (navigator.vibrate) navigator.vibrate(30);
              setMasques((m) => [...m, c.id]);
              supprimer(c.id);
            }}
            style={{ width: '38px', height: '38px', borderRadius: '9px', border: '1px solid #FCA5A5', background: '#fff', color: '#B91C1C', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
            <Trash2 size={16} />
          </button>
        </div>
      )}
    </div>
  );

  if (bloque) {
    return (
      <div style={{ opacity: 0.55 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '6px' }}>
          <Lock size={17} color="#9CA3AF" />
          <span style={{ fontSize: '15px', fontWeight: 500, color: '#6B7280' }}>Numéros de série</span>
        </div>
        <div style={{ fontSize: '13px', color: '#9CA3AF' }}>
          {messageBloque || 'Disponible plus tard'}
        </div>
      </div>
    );
  }

  return (
    <div>
      {erreur && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#FEE2E2', color: '#991B1B', padding: '11px', borderRadius: '8px', marginBottom: '12px', fontSize: '13px' }}>
          <AlertCircle size={15} /> {erreur}
        </div>
      )}
      {succes && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#ECFDF5', color: '#065F46', padding: '11px', borderRadius: '8px', marginBottom: '12px', fontSize: '13px' }}>
          <Check size={15} /> {succes}
        </div>
      )}

      <div style={bloc}>
        <div style={{ fontSize: '11px', color: '#9CA3AF', letterSpacing: '.4px', marginBottom: '9px' }}>
          PANNEAUX PAR MICRO-ONDULEUR
        </div>
        <div style={{ display: 'flex', gap: '9px', marginBottom: '6px' }}>
          {[1, 2].map((v) => {
            const fige = (data.micro_onduleurs || []).length > 0;
            return (
              <button key={v} disabled={fige && parMicro !== v}
                onClick={async () => {
                  setMsgConfig('');
                  try {
                    await axios.post(`${API}/api/chantiers/${chantierId}/config-micro`, { panneaux_par_micro: v });
                    setParMicro(v);
                    charger();
                  } catch (e) {
                    setMsgConfig(e.response?.data?.detail || 'Modification impossible');
                  }
                }}
                style={{
                  flex: 1, padding: '12px', borderRadius: '10px', fontSize: '14px',
                  cursor: (fige && parMicro !== v) ? 'default' : 'pointer',
                  border: parMicro === v ? '1.5px solid #10B981' : '1px solid #E5E7EB',
                  background: parMicro === v ? '#ECFDF5' : '#fff',
                  color: parMicro === v ? '#065F46' : (fige ? '#D1D5DB' : '#6B7280')
                }}>
                {v} panneau{v > 1 ? 'x' : ''}
              </button>
            );
          })}
        </div>
        {msgConfig && (
          <div style={{ fontSize: '12px', color: '#B45309', marginBottom: '8px', lineHeight: 1.4 }}>{msgConfig}</div>
        )}
        <div style={{ height: '1px', background: '#F3F4F6', margin: '14px 0' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <span style={{ fontSize: '15px', fontWeight: 500 }}>
            Micro-onduleurs
            <span style={{ color: data.nb_attendus && data.nb_micro_onduleurs >= data.nb_attendus ? '#10B981' : '#9CA3AF', marginLeft: '6px' }}>
              {data.nb_micro_onduleurs}{data.nb_attendus ? ` / ${data.nb_attendus}` : ''}
            </span>
          </span>
        </div>

        {[...data.micro_onduleurs, ...provisoires.filter((p) => p.type_materiel === 'micro_onduleur')].map(ligne)}

        {!verrouille && (() => {
          const complet = data.nb_attendus && data.nb_micro_onduleurs >= data.nb_attendus;
          return (
            <>
              <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                <button disabled={complet}
                  onClick={() => { if (!complet) demarrerScan('micro_onduleur'); }}
                  style={{ ...btnVert, background: complet ? '#F3F4F6' : '#10B981', color: complet ? '#9CA3AF' : '#fff', cursor: complet ? 'default' : 'pointer' }}>
                  <ScanLine size={17} /> Scanner
                </button>
                <button disabled={complet}
                  onClick={() => { if (complet) return; typeRef.current = 'micro_onduleur'; setTypeEnCours('micro_onduleur'); ouvrirFenetre(); setSaisieOuverte(true); }}
                  style={{ ...btnGris, width: 'auto', paddingLeft: '16px', paddingRight: '16px', color: complet ? '#D1D5DB' : '#374151', cursor: complet ? 'default' : 'pointer' }}>
                  <Keyboard size={17} />
                </button>
              </div>
              {complet && (
                <div style={{ fontSize: '12px', color: '#059669', marginTop: '9px', textAlign: 'center' }}>
                  Les {data.nb_attendus} micro-onduleurs sont scannés
                </div>
              )}
            </>
          );
        })()}
      </div>

      <div style={bloc}>
        <div style={{ fontSize: '15px', fontWeight: 500, marginBottom: '10px' }}>
          Passerelle
          <span style={{ color: data.passerelles.length ? '#10B981' : '#9CA3AF', marginLeft: '6px' }}>{data.passerelles.length}</span>
        </div>

        {[...data.passerelles, ...provisoires.filter((p) => p.type_materiel === 'passerelle')].map(ligne)}

        {!verrouille && (
          <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
            <button disabled={microsIncomplets || (data.passerelles || []).length > 0}
              onClick={() => { if (!microsIncomplets && !(data.passerelles || []).length) demarrerScan('passerelle'); }}
              style={{ ...btnVert, background: (microsIncomplets || (data.passerelles || []).length) ? '#F3F4F6' : '#10B981', color: (microsIncomplets || (data.passerelles || []).length) ? '#9CA3AF' : '#fff' }}>
              <ScanLine size={17} /> Scanner
            </button>
            <button disabled={microsIncomplets || (data.passerelles || []).length > 0}
              onClick={() => { if (microsIncomplets || (data.passerelles || []).length) return; typeRef.current = 'passerelle'; setTypeEnCours('passerelle'); ouvrirFenetre(); setSaisieOuverte(true); }}
              style={{ ...btnGris, width: 'auto', paddingLeft: '16px', paddingRight: '16px', color: (data.passerelles || []).length ? '#D1D5DB' : '#374151' }}>
              <Keyboard size={17} />
            </button>
          </div>
        )}
        {microsIncomplets && (
          <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '9px', textAlign: 'center' }}>
            Disponible une fois les micro-onduleurs scannés
          </div>
        )}
        {(data.passerelles || []).length > 0 && (
          <div style={{ fontSize: '12px', color: '#059669', marginTop: '9px', textAlign: 'center' }}>
            La passerelle est scannée
          </div>
        )}
      </div>


      {aForcer && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '14px', padding: '22px', width: '100%', maxWidth: '340px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '12px' }}>
              <AlertCircle size={22} color="#DC2626" />
              <span style={{ fontSize: '17px', fontWeight: 600, color: '#991B1B' }}>Numéro suspect</span>
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: '18px', textAlign: 'center', padding: '13px', background: '#FEE2E2', color: '#991B1B', borderRadius: '9px', marginBottom: '14px', letterSpacing: '0.5px' }}>
              {aForcer.numero}
            </div>
            <div style={{ fontSize: '14px', color: '#991B1B', lineHeight: 1.55, marginBottom: '10px', fontWeight: 500 }}>
              Ce numéro ne suit pas la même référence que les autres micro-onduleurs.
            </div>
            <div style={{ fontSize: '13px', color: '#6B7280', lineHeight: 1.5, marginBottom: '18px' }}>
              {aForcer.message}. Il s'agit peut-être d'une autre marque ou d'un mauvais code-barres. Vérifiez avant de poursuivre.
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setAForcer(null)} style={{ ...btnGris, flex: 1 }}>Annuler</button>
              <button onClick={async () => {
                  const f = aForcer;
                  setAForcer(null);
                  try {
                    if (f.remplace) {
                      await axios.put(`${API}/api/chantiers/${chantierId}/codes-barres/${f.remplace}`, { numero: f.numero, forcer: true });
                      setARemplacer(null);
                    } else {
                      await axios.post(`${API}/api/chantiers/${chantierId}/codes-barres`, {
                        numero: f.numero, type_materiel: f.type, saisie_manuelle: f.manuelle, forcer: true
                      });
                    }
                    charger();
                  } catch (e) {
                    setErreur(e.response?.data?.detail || "Échec de l'enregistrement");
                  }
                }}
                style={{ ...btnVert, flex: 1, background: '#DC2626' }}>
                Enregistrer quand même
              </button>
            </div>
          </div>
        </div>
      )}

      {modif && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 96, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '14px', padding: '22px', width: '100%', maxWidth: '360px' }}>
            <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '5px' }}>Corriger le numéro</div>
            <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px' }}>
              Placez le curseur où vous voulez pour corriger.
            </div>
            <input
              value={nouveau}
              autoFocus
              onChange={(e) => setNouveau(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 24))}
              style={{ width: '100%', padding: '14px', border: '1.5px solid #E5E7EB', borderRadius: '10px', fontSize: '19px', fontFamily: 'monospace', textAlign: 'center', letterSpacing: '1.5px', marginBottom: '8px', outline: 'none' }} />
            {(() => {
              const autres = (data?.micro_onduleurs || []).filter((c) => c.id !== modif.id);
              const estMicro = (data?.micro_onduleurs || []).some((c) => c.id === modif.id);
              const ref = (estMicro && autres.length) ? (autres[0].numero || '').length : null;
              const ecart = ref ? ref - nouveau.length : 0;
              const bon = !ref || ecart === 0;
              return (
                <div style={{ fontSize: '12px', color: bon ? '#10B981' : '#B45309', textAlign: 'center', marginBottom: '16px' }}>
                  {nouveau.length} caractère{nouveau.length > 1 ? 's' : ''}
                  {ref && ecart > 0 && ` · il manque ${ecart} caractère${ecart > 1 ? 's' : ''}`}
                  {ref && ecart < 0 && ` · ${-ecart} caractère${-ecart > 1 ? 's' : ''} en trop`}
                </div>
              );
            })()}
            {erreur && (
              <div style={{ fontSize: '13px', color: '#991B1B', marginBottom: '12px', textAlign: 'center' }}>{erreur}</div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => { setModif(null); setErreur(''); }} style={{ ...btnGris, flex: 1 }}>
                Annuler
              </button>
              <button disabled={(() => {
                  if (nouveau.length < 6 || nouveau === modif.numero) return true;
                  const autres = (data?.micro_onduleurs || []).filter((c) => c.id !== modif.id);
                  const estMic2 = (data?.micro_onduleurs || []).some((c) => c.id === modif.id);
                  if (estMic2 && autres.length) {
                    return nouveau.length !== (autres[0].numero || '').length;
                  }
                  return false;
                })()}
                onClick={async () => {
                  setErreur('');
                  try {
                    await axios.put(`${API}/api/chantiers/${chantierId}/codes-barres/${modif.id}`, { numero: nouveau });
                    setModif(null);
                    charger();
                  } catch (e) {
                    setErreur(e.response?.data?.detail || 'Échec de la correction');
                  }
                }}
                style={{ ...btnVert, flex: 1, opacity: (() => {
                  if (nouveau.length < 6 || nouveau === modif.numero) return 0.45;
                  const autres = (data?.micro_onduleurs || []).filter((c) => c.id !== modif.id);
                  const estMic = (data?.micro_onduleurs || []).some((c) => c.id === modif.id);
                  if (estMic && autres.length && nouveau.length !== (autres[0].numero || '').length) return 0.45;
                  return 1;
                })() }}>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {saisieOuverte && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 95, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '14px', padding: '22px', width: '100%', maxWidth: '340px' }}>
            <div style={{ fontSize: '17px', fontWeight: 600, marginBottom: '4px' }}>
              {typeEnCours === 'passerelle' ? 'Passerelle' : 'Micro-onduleur'}
            </div>
            <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '14px' }}>Saisissez le numéro de série</div>
            <input value={saisie} onChange={(e) => setSaisie(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 24))}
              placeholder="705000245584" autoFocus autoCapitalize="characters" autoCorrect="off" spellCheck="false"
              style={{ width: '100%', padding: '13px', border: '1px solid #D1D5DB', borderRadius: '10px', fontSize: '19px', fontFamily: 'monospace', letterSpacing: '1px', marginBottom: '6px', textAlign: 'center' }} />
            <div style={{ fontSize: '12px', color: saisie.length >= 6 ? '#10B981' : '#9CA3AF', textAlign: 'center', marginBottom: '16px' }}>
              {saisie.length} caractère{saisie.length > 1 ? 's' : ''}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => { fermerFenetre(); setSaisieOuverte(false); setSaisie(''); }} style={{ ...btnGris }}>Annuler</button>
              <button disabled={saisie.length < 6}
                onClick={() => {
                  const v = saisie.toUpperCase().replace(/[^A-Z0-9]/g, '');
                  setSaisie('');
                  fermerFenetre();
                  setSaisieOuverte(false);
                  setProvisoires((p) => [...p, { id: 'tmp_' + Date.now(), numero: v, type_materiel: typeRef.current, saisie_manuelle: true, provisoire: true }]);
                  enregistrer(v, true);
                }}
                style={{ ...btnVert, opacity: saisie.length >= 6 ? 1 : 0.45 }}>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {scanOuvert && (
        <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 100, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', color: '#fff' }}>
            <span style={{ fontSize: '15px' }}>{typeEnCours === 'passerelle' ? 'Passerelle' : 'Micro-onduleur'}</span>
            <X size={26} style={{ cursor: 'pointer' }} onClick={arreterScan} />
            {confirme && (
              <div style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)',
                backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
                zIndex: 300, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', padding: '30px'
              }}>
                <div style={{
                  width: '72px', height: '72px', borderRadius: '50%', background: '#10B981',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px'
                }}>
                  <Check size={38} color="#fff" strokeWidth={2.6} />
                </div>
                <div style={{ fontSize: '18px', color: '#fff', marginBottom: '8px', textAlign: 'center' }}>
                  Numéro enregistré
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: '15px', color: 'rgba(255,255,255,0.75)' }}>
                  {confirme}
                </div>
              </div>
            )}
          </div>

          <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            <video ref={video} style={{ width: '100%', height: '100%', objectFit: 'cover' }} playsInline muted />
            <div style={{ position: 'absolute', top: '50%', left: '8%', right: '8%', height: '110px', transform: 'translateY(-50%)', border: '2px solid #10B981', borderRadius: '10px', boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)' }} />
          </div>

          <div style={{ padding: '18px 16px 26px', color: '#fff', textAlign: 'center' }}>
            <div style={{ fontSize: '14px', opacity: 0.85, marginBottom: '14px' }}>
              Placez le code-barres dans le cadre
            </div>
            {succes && <div style={{ color: '#6EE7B7', fontSize: '15px', marginBottom: '12px' }}>{succes}</div>}
            {erreur && <div style={{ color: '#FCA5A5', fontSize: '14px', marginBottom: '12px' }}>{erreur}</div>}
            <button onClick={arreterScan} style={{ ...btnGris, background: 'rgba(255,255,255,0.14)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)' }}>
              Terminer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TechCodesBarres;
